import React, { useState } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { App } from "antd5";
import { useLocation } from "wouter";

import OpportunityDiscardModal from "components/opportunities/delete_opportunity_modal/OpportunityDiscardModal";
import OpportunityContent from "components/opportunities/OpportunityContent";
import { ScheduleReminderButton } from "components/reminders/ScheduleReminderButton";
import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import EmptyState from "components/ui/empty_state/EmptyState";
import { useBuyerActivityStatus } from "lib/hooks/api/opportunities/useBuyerActivityStatus";
import { useDeleteOpportunity } from "lib/hooks/api/opportunities/useDeleteOpportunity";
import { sysPrimaryDefault } from "lib/themes/colors";
import { OpportunityBuyerActivityStatus } from "lib/types/graphQLEnums";
import OpportunityCommentsSidebar from "../../components/opportunities/opportunity_comments_sidebar/OpportunityCommentsSidebar";
import OpportunitySavedSidebar from "../../components/opportunities/opportunity_saved_sidebar/OpportunitySavedSidebar";
import Tabs, { TabItem } from "../../components/ui/tabs/Tabs";
import CentredSpinner from "../../lib/core_components/CentredSpinner";
import { useOpportunity } from "../../lib/hooks/api/opportunities/useOpportunity";
import { Flex, Text } from "../../styles/utility-components";

type OpportunityPageProps = {
  id: string;
};

export default function OpportunityPage({ id }: OpportunityPageProps) {
  const { message } = App.useApp();
  const theme = useTheme();
  const [, setLocation] = useLocation();

  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const { mutate: deleteOpportunity } = useDeleteOpportunity({
    onSuccess: () => {
      message.success("Opportunity deleted successfully");
      window.location.href = "/opportunities";
    },
  });

  const handleDeleteOpportunity = () => {
    setIsDiscardModalOpen(true);
  };

  const handleDiscardModalSubmit = () => {
    deleteOpportunity({ ids: [id] });
    setIsDiscardModalOpen(false);
  };

  const handleDiscardModalCancel = () => {
    setIsDiscardModalOpen(false);
  };

  const handleFeedback = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("userflow", "9f97793b-ef2e-4e7b-a5e6-1bc3a0a7d5a0");
    window.open(url.toString(), "_blank");
  };

  const { data: opportunity, isLoading, isError } = useOpportunity(id);

  useBuyerActivityStatus({
    opportunityId: id,
    enabled: opportunity?.buyerActivity?.status === OpportunityBuyerActivityStatus.Pending,
  });

  if (isLoading) {
    return <CentredSpinner />;
  }

  if (isError) {
    return (
      <Flex flexGrow={1} alignItems="center">
        <EmptyState
          type="loadError"
          heading="Sorry something has gone wrong loading this opportunity"
          description="If this error persists please contact support"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  if (!opportunity) {
    return (
      <Flex flexGrow={1} alignItems="center">
        <EmptyState
          type="loadError"
          heading="Opportunity not found"
          description="The opportunity you are looking for does not exist."
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  const tabItems: TabItem[] = [
    {
      key: "saved",
      label: "Saved",
      icon: "bookmark02",
      children: <OpportunitySavedSidebar opportunity={opportunity} />,
    },
    {
      key: "comments",
      label: "Comments",
      icon: "messageTextSquare",
      children: <OpportunityCommentsSidebar opportunityId={opportunity.id} />,
    },
  ];

  return (
    <Container>
      <BreadcrumbContainer>
        <Flex alignItems="center" gap={8}>
          <BreadcrumbButton
            color={sysPrimaryDefault}
            onClick={() => {
              if (
                history.state &&
                history.state.from === "/" &&
                history.state.previousSearchParams
              ) {
                return setLocation(`/?${history.state.previousSearchParams}`);
              }
              setLocation("/");
            }}
          >
            Opportunities
          </BreadcrumbButton>
          <Text color={theme.colors.sysTextSecondary}> / </Text>
          <Text color={theme.colors.sysTextDefault}>{opportunity?.name}</Text>
        </Flex>
        <Flex gap={8}>
          <ScheduleReminderButton
            dataType="opportunity"
            anchorDate={opportunity?.closeDate}
            dataId={opportunity?.id ?? ""}
            dataTitle={opportunity?.name ?? ""}
            anchorType="close"
            buttonRender={
              <Button
                buttonType="secondary"
                icon="clockRewind"
                iconProps={{ color: "sysPrimaryDefault" }}
              >
                Set a reminder
              </Button>
            }
          />
          <DropdownMenu
            align="end"
            width="fit-content"
            trigger={
              <DropdownMenuTrigger
                buttonStyle="inverse"
                icon="dotsVertical"
                hideArrow
                aria-label="Opportunity actions"
              />
            }
          >
            <DropdownMenuItem
              key="delete"
              onClick={handleDeleteOpportunity}
              icon="trash01"
              iconProps={{ color: "sysStatusNegative" }}
            >
              Delete
            </DropdownMenuItem>
            <DropdownMenuItem
              key="feedback"
              onClick={handleFeedback}
              icon="linkExternal02"
              iconProps={{ color: "sysPrimaryDefault" }}
            >
              We'd love your feedback
            </DropdownMenuItem>
          </DropdownMenu>
        </Flex>
      </BreadcrumbContainer>
      <Flex height="100%">
        <Content>
          <Flex justifyContent="space-between" height="100%">
            <OpportunityContent opportunity={opportunity} />
            <Sidebar>
              <Tabs items={tabItems} defaultValue={tabItems[0].key} fullWidth />
            </Sidebar>
          </Flex>
        </Content>
      </Flex>
      <OpportunityDiscardModal
        isOpen={isDiscardModalOpen}
        onCancel={handleDiscardModalCancel}
        onSubmit={handleDiscardModalSubmit}
      />
    </Container>
  );
}

const Container = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  width: 480,
  height: "100vh",
  overflow: "hidden",
});

const BreadcrumbContainer = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px",
  borderBottom: `1px solid ${theme.colors.sysBorderPrimary}`,
}));

const BreadcrumbButton = styled.button<{ color?: string }>(({ color }) => ({
  all: "unset",
  cursor: "pointer",
  padding: 0,
  color: color ?? "inherit",
}));

const Content = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  width: "100%",
  height: "calc(100vh - 48px)", // Subtract breadcrumb height
  overflow: "hidden",
});

const Sidebar = styled(Flex)(({ theme }) => ({
  width: 440,
  flexShrink: 0,
  borderLeft: `1px solid ${theme.colors.sysBorderPrimary}`,
  height: "100%",
  flexDirection: "column",
  overflow: "hidden",
}));
